import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  LogOut, Search, Download, Users, UserCheck, UserPlus, Phone, 
  Filter, RefreshCw, CreditCard, DollarSign, Percent, Package, Award, Briefcase
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

// Import modular admin components
import { 
  TeamManagement, 
  ProductManagement, 
  PricingOffers, 
  CommissionSettings, 
  Leaderboard, 
  CreditManagement,
  RoleBadge 
} from "../components/admin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stats Card Component
const StatCard = ({ icon: Icon, label, value, color, subtitle }) => (
  <div className="card p-6 border border-white/10 flex items-center gap-4">
    <div className={`w-12 h-12 flex items-center justify-center ${color}`}>
      <Icon className="w-6 h-6" />
    </div>
    <div>
      <p className="text-white/50 text-sm">{label}</p>
      <p className="text-2xl font-bold text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
      {subtitle && <p className="text-xs text-white/40">{subtitle}</p>}
    </div>
  </div>
);

// Status Badge Component
const StatusBadge = ({ status }) => {
  const styles = {
    new: "status-badge status-new",
    contacted: "status-badge status-contacted",
    converted: "status-badge status-converted"
  };
  return <span className={styles[status] || styles.new}>{status}</span>;
};

// Leads Management Component
const LeadsManagement = ({ token, userRole }) => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/leads?${params.toString()}`, { headers }),
        axios.get(`${API}/admin/stats`, { headers })
      ]);
      
      setLeads(leadsRes.data.leads);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [search, statusFilter]);

  const updateStatus = async (leadId, newStatus) => {
    try {
      await axios.post(`${API}/admin/update-status`, { lead_id: leadId, status: newStatus }, { headers });
      toast.success("Updated");
      fetchData();
    } catch { toast.error("Failed"); }
  };

  const exportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/export-csv`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'ptv-leads.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Downloaded");
    } catch { toast.error("Failed"); }
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { 
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' 
  });

  if (loading) return <div className="text-center text-white/50 py-12">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="leads-management">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Leads" value={stats.total} color="bg-[#D4AF37]/20 text-[#D4AF37]" />
        <StatCard icon={UserPlus} label="New" value={stats.new} color="bg-blue-500/20 text-blue-400" />
        <StatCard icon={Phone} label="Contacted" value={stats.contacted} color="bg-yellow-500/20 text-yellow-400" />
        <StatCard icon={UserCheck} label="Converted" value={stats.converted} color="bg-green-500/20 text-green-400" />
      </div>

      {/* Search and Filters */}
      <div className="card p-4 border border-white/10 flex flex-col md:flex-row items-center gap-4">
        <div className="relative flex-1 w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
          <Input 
            type="text" 
            placeholder="Search..." 
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
            className="input-dark w-full pl-11"
            data-testid="search-leads"
          />
        </div>
        <div className="flex items-center gap-4 w-full md:w-auto">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px] input-dark" data-testid="status-filter">
              <Filter className="w-4 h-4 mr-2" /><SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="ghost" onClick={fetchData} className="text-white/70" data-testid="refresh-leads">
            <RefreshCw className="w-5 h-5" />
          </Button>
          {['admin', 'director'].includes(userRole) && (
            <Button onClick={exportCSV} className="btn-primary flex items-center gap-2" data-testid="export-csv-btn">
              <Download className="w-4 h-4" /> Export
            </Button>
          )}
        </div>
      </div>

      {/* Leads Table */}
      <div className="card border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="admin-table" data-testid="leads-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Add-ons</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-12 text-white/50">No leads</td></tr>
              ) : (
                leads.map((lead) => (
                  <tr key={lead.id} data-testid={`lead-row-${lead.id}`}>
                    <td className="font-medium text-white">{lead.name}</td>
                    <td className="text-white/70">{lead.email}</td>
                    <td className="text-white/70">{lead.phone}</td>
                    <td>
                      {lead.addons?.length > 0 
                        ? lead.addons.map((a, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] mr-1">{a}</span>
                          )) 
                        : <span className="text-white/30">-</span>
                      }
                    </td>
                    <td><StatusBadge status={lead.status} /></td>
                    <td className="text-white/50">{formatDate(lead.created_at)}</td>
                    <td>
                      <Select value={lead.status} onValueChange={(v) => updateStatus(lead.id, v)}>
                        <SelectTrigger className="w-[120px] h-8 text-xs input-dark" data-testid={`status-select-${lead.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-white/10">
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="converted">Converted</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("ptv_admin_token");
  const user = JSON.parse(localStorage.getItem("ptv_user") || '{}');
  const userRole = user.role || 'admin';

  useEffect(() => {
    if (!token) {
      navigate("/admin");
    } else {
      setLoading(false);
    }
  }, [token, navigate]);

  const handleLogout = () => {
    localStorage.removeItem("ptv_admin_token");
    localStorage.removeItem("ptv_user");
    navigate("/admin");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-[#D4AF37] animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#121212] border-b border-white/10 px-6 py-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-['Playfair_Display'] text-2xl font-bold">
              Prestigious<span className="text-[#D4AF37]">TV</span>
            </Link>
            <span className="text-white/30">|</span>
            <span className="text-white/50 text-sm">Admin Dashboard</span>
            <RoleBadge role={userRole} />
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/50 text-sm hidden md:block">{user.email || 'admin@ptv.com'}</span>
            <Button 
              variant="ghost" 
              onClick={handleLogout} 
              className="text-white/70 hover:text-[#D4AF37]"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="leads" className="space-y-6">
          <TabsList className="bg-[#121212] border border-white/10 p-1 flex-wrap">
            <TabsTrigger value="leads" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black" data-testid="tab-leads">
              <Users className="w-4 h-4 mr-2" /> Leads
            </TabsTrigger>
            
            {['admin', 'director'].includes(userRole) && (
              <>
                <TabsTrigger value="team" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black" data-testid="tab-team">
                  <Briefcase className="w-4 h-4 mr-2" /> Team
                </TabsTrigger>
                <TabsTrigger value="products" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black" data-testid="tab-products">
                  <Package className="w-4 h-4 mr-2" /> Products
                </TabsTrigger>
                <TabsTrigger value="pricing" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black" data-testid="tab-pricing">
                  <DollarSign className="w-4 h-4 mr-2" /> Pricing
                </TabsTrigger>
              </>
            )}
            
            {userRole === 'admin' && (
              <TabsTrigger value="commissions" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black" data-testid="tab-commissions">
                <Percent className="w-4 h-4 mr-2" /> Commissions
              </TabsTrigger>
            )}
            
            <TabsTrigger value="leaderboard" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black" data-testid="tab-leaderboard">
              <Award className="w-4 h-4 mr-2" /> Leaderboard
            </TabsTrigger>
            
            <TabsTrigger value="credits" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black" data-testid="tab-credits">
              <CreditCard className="w-4 h-4 mr-2" /> Credits
            </TabsTrigger>
          </TabsList>

          {/* Tab Contents */}
          <TabsContent value="leads">
            <LeadsManagement token={token} userRole={userRole} />
          </TabsContent>

          {['admin', 'director'].includes(userRole) && (
            <>
              <TabsContent value="team">
                <TeamManagement token={token} userRole={userRole} />
              </TabsContent>
              <TabsContent value="products">
                <ProductManagement token={token} />
              </TabsContent>
              <TabsContent value="pricing">
                <PricingOffers token={token} />
              </TabsContent>
            </>
          )}

          {userRole === 'admin' && (
            <TabsContent value="commissions">
              <CommissionSettings token={token} />
            </TabsContent>
          )}

          <TabsContent value="leaderboard">
            <Leaderboard token={token} />
          </TabsContent>
          
          <TabsContent value="credits">
            <CreditManagement />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
